
import React, { useState } from 'react';
import { Helmet } from 'react-helmet';

const supportSections = [
  {
    title: "Contact Information",
    content: (
      <>
        <h2 className="text-white text-2xl font-bold mb-4">Contact Information</h2>
        <ul>
          <li><strong>Phone:</strong> +1 404 282 7007</li>
          <li><strong>Email:</strong> info@aletheiasystems.com</li>
          <li><strong>Address:</strong> 260 Peachtree Street NW, Atlanta, GA 30303</li>
        </ul>
      </>
    ),
  },
  {
    title: "Technical Support Overview",
    content: (
      <>
        <h2 className="text-white text-2xl font-bold mb-4">Technical Support Overview</h2>
        <p>Your Maintenance offering entitles you to access Aletheia Systems technical support expertise Monday – Friday from 9 am – 6 pm (Eastern Time). Note: After-hours support is available for emergency instances only (e.g., production is down). Aletheia Systems' technical support is designed to maximize uptime and reduce your internal IT costs. Our Enterprise Customer Support organization is dedicated to resolving your issues quickly and effectively. We view every support engagement as an opportunity to demonstrate our commitment to customer satisfaction. You can count on us to help you receive the most from your Aletheia Systems technology investments.</p>
      </>
    ),
  },
  {
    title: "Support Resources",
    content: (
      <>
        <h2 className="text-white text-2xl font-bold mb-4">Support Resources</h2>
        <ul>
          <li>Terms & Conditions</li>
          <li>Documentation</li>
          <li>Eligibility for Support</li>
          <li>Support Delivery Types</li>
          <li>Designated Contacts</li>
          <li>Severity Levels</li>
          <li>Case Handling</li>
          <li>Response Times</li>
          <li>Escalation Process</li>
          <li>Maintenance Policies</li>
          <li>Consulting</li>
        </ul>
      </>
    ),
  },
  {
    title: "Terms & Conditions",
    content: (
      <>
        <h2 className="text-white text-2xl font-bold mb-4">Terms & Conditions</h2>
        <p>Aletheia Systems' Technical Support Terms & Conditions describe the scope of and conditions under which Aletheia Systems supplies technical support to customers (customers with current Maintenance subscription that includes technical support) for those Aletheia Systems products that are used in a supported configuration and in accordance with the terms of their License Agreement, documentation, and associated policies, including the End of Life Policy.</p>
      </>
    ),
  },
  {
    title: "Documentation",
    content: (
      <>
        <h2 className="text-white text-2xl font-bold mb-4">Documentation</h2>
        <p>This guide contains important information on the procedures and practices related to obtaining technical support for your Aletheia Systems products. Following the processes described in this document will improve your support experience when contacting us for assistance or when using our online resources. Aletheia Systems reserves the right to make changes to this document and related processes at any time.</p>
      </>
    ),
  },
  {
    title: "Eligibility for Support",
    content: (
      <>
        <h2 className="text-white text-2xl font-bold mb-4">Eligibility for Support</h2>
        <p>Customers must have current Support/Maintenance for their product to access technical support. This document does not replace the contractual terms and conditions under which you acquired specific Aletheia Systems products and/or technical support. Users are recommended to create a login to the Support Portal to be able to submit support tickets to the technical support team.</p>
      </>
    ),
  },
  {
    title: "Support Delivery Types",
    content: (
      <>
        <h2 className="text-white text-2xl font-bold mb-4">Support Delivery Types</h2>
        <p>Support is delivered via email, phone, and the support portal. Our team ensures timely and effective resolution of your issues to minimize downtime and maintain operational efficiency.</p>
      </>
    ),
  },
  {
    title: "Designated Contacts",
    content: (
      <>
        <h2 className="text-white text-2xl font-bold mb-4">Designated Contacts</h2>
        <p>Each organization must designate specific contacts who will interact with Aletheia Systems support. This ensures streamlined communication and proper handling of support cases.</p>
      </>
    ),
  },
  {
    title: "Severity Levels",
    content: (
      <>
        <h2 className="text-white text-2xl font-bold mb-4">Severity Levels</h2>
        <p>Support requests are categorized based on severity levels: Critical, High, Medium, and Low. This helps prioritize cases based on urgency and business impact.</p>
      </>
    ),
  },
  {
    title: "Case Handling",
    content: (
      <>
        <h2 className="text-white text-2xl font-bold mb-4">Case Handling</h2>
        <p>Each case is assigned a unique identifier and handled by our experienced technical team. We provide regular updates on case progress and resolution steps.</p>
      </>
    ),
  },
  {
    title: "Response Times",
    content: (
      <>
        <h2 className="text-white text-2xl font-bold mb-4">Response Times</h2>
        <p>Our response times vary by severity level. Critical issues are addressed within 1 hour, while lower-severity issues are handled within standard response timelines.</p>
      </>
    ),
  },
  {
    title: "Escalation Process",
    content: (
      <>
        <h2 className="text-white text-2xl font-bold mb-4">Escalation Process</h2>
        <p>If a case requires further attention, it is escalated to senior support engineers or management to ensure timely resolution.</p>
      </>
    ),
  },
  {
    title: "Maintenance Policies",
    content: (
      <>
        <h2 className="text-white text-2xl font-bold mb-4">Maintenance Policies</h2>
        <p>Maintenance includes regular updates, bug fixes, and access to new features. Customers are encouraged to stay current with their maintenance subscription to maximize benefits.</p>
      </>
    ),
  },
  {
    title: "Consulting",
    content: (
      <>
        <h2 className="text-white text-2xl font-bold mb-4">Consulting</h2>
        <p>Aletheia Systems offers consulting services for custom integrations, system optimizations, and advanced configurations to meet your unique business needs.</p>
      </>
    ),
  },
];

const SupportPage = () => {
  const [activeSection, setActiveSection] = useState(0); // Default to the first section

  return (
    <div className="bg-secondary text-text-light py-24">
      <Helmet>
        <title>Support & Technical Assistance | Aletheia Systems</title>
        <meta name="description" content="Get technical support and assistance with Aletheia Systems products. Our support team is available to help you with any issues or questions you may have." />
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row gap-8">
        {/* Sidebar Navigation */}
        <nav className="lg:w-1/4 bg-[#0d1222] p-6 rounded-lg shadow-lg">
          <h2 className="text-white text-3xl font-bold mb-6">Support Topics</h2>
          <ul>
            {supportSections.map((section, index) => (
              <li key={index} className="mb-4">
                <button
                  className={`w-full text-left text-lg py-2 px-4 rounded-md transition duration-300 ${activeSection === index ? 'bg-primary text-white' : 'text-text-light hover:bg-gray-700'}`}
                  onClick={() => setActiveSection(index)}
                >
                  {section.title}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Main Content Area */}
        <div className="lg:w-3/4 bg-[#0d1222] p-8 rounded-lg shadow-lg">
          {supportSections[activeSection] && (
            <div className="prose prose-invert max-w-none">
              {supportSections[activeSection].content}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportPage;
